// DeliverableCard — renders a marketing deliverable as an approve/comment card
// (message_type Deliverable). Paints from the message metadata first (so it always
// looks complete, even offline), then fetches live status from TMCHub so approval
// state stays correct across reloads and multiple approvers. All client channel
// members can approve or leave a comment (request changes).
//
// Reuses the .signoff / .btn / .status-chip classes (no new CSS) + a little inline
// layout for the image / carousel / video preview.

import { useCallback, useEffect, useState } from "react";
import { DeliverablesApi } from "../hooks/useApi.js";

function fromMeta(message) {
  let meta = {};
  try { meta = JSON.parse(message.metadataJson || "{}"); } catch { /* ignore */ }
  if (!meta.deliverableId) return null;
  return {
    deliverableId: meta.deliverableId,
    title: meta.title || "Creative",
    kind: meta.deliverableKind || "image",
    assetUrl: meta.assetUrl || null,
    thumbnailUrl: meta.thumbnailUrl || null,
    frames: Array.isArray(meta.frames) ? meta.frames : [],
    caption: meta.caption || "",
    status: "submitted",
    reviewNote: null,
    approvedBy: null,
    canAct: true,
  };
}

const STATUS = {
  approved: { chip: "done", label: "✓ Approved" },
  changes_requested: { chip: "partial", label: "Changes requested" },
  submitted: { chip: "pending", label: "Awaiting approval" },
};

export default function DeliverableCard({ message, onDelete }) {
  const [doc, setDoc] = useState(() => fromMeta(message));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removed, setRemoved] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");

  const refresh = useCallback(async () => {
    if (!doc?.deliverableId) return;
    try {
      const live = await DeliverablesApi.get(doc.deliverableId);
      if (live) setDoc((d) => ({ ...d, ...live, frames: live.frames?.length ? live.frames : d.frames }));
    } catch (e) { if (e?.status === 404) setRemoved(true); /* else keep the cached card */ }
  }, [doc?.deliverableId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function approve() {
    setBusy(true); setError("");
    try {
      const r = await DeliverablesApi.approve(doc.deliverableId, message.channelId);
      if (r?.deliverableId) setDoc((d) => ({ ...d, ...r })); else await refresh();
    } catch (e) { setError(e.body?.error || "Could not approve right now."); }
    finally { setBusy(false); }
  }

  async function sendComment() {
    const text = comment.trim();
    if (text.length < 2) return;
    setBusy(true); setError("");
    try {
      const r = await DeliverablesApi.comment(doc.deliverableId, message.channelId, text);
      setComment(""); setShowComment(false);
      if (r?.deliverableId) setDoc((d) => ({ ...d, ...r })); else await refresh();
    } catch (e) { setError(e.body?.error || "Could not send your comment."); }
    finally { setBusy(false); }
  }

  if (!doc) return (
    <div className="msg-system-row">
      <div className="msg-system">🎬 Creative for approval</div>
      {onDelete && <button type="button" className="msg-system__del" title="Remove this card" onClick={() => onDelete(message)}>✕</button>}
    </div>
  );

  if (removed) return (
    <div className="signoff signoff--removed">
      <div className="signoff__head">
        <span className="signoff__icon">🗑️</span>
        <div className="signoff__doc"><h4>{doc.title}</h4><span className="ver">Creative</span></div>
        <span className="status-chip status-chip--open">Removed</span>
        {onDelete && <button type="button" className="meetingcard__del" title="Remove this card" onClick={() => onDelete(message)}>✕</button>}
      </div>
      <p className="signoff__removed">This creative was removed and is no longer available.</p>
    </div>
  );

  const st = STATUS[doc.status] || STATUS.submitted;
  const frames = doc.frames?.length ? doc.frames : (doc.assetUrl ? [doc.assetUrl] : []);
  const isCarousel = doc.kind === "carousel" && frames.length > 1;
  const isVideo = doc.kind === "video";

  return (
    <div className="signoff">
      <div className="signoff__head">
        <span className="signoff__icon">🎬</span>
        <div className="signoff__doc">
          <span className="signoff__eyebrow">Creative for approval</span>
          <h4>{doc.title}</h4>
          {isCarousel && <span className="ver">Carousel · {frames.length} slides</span>}
        </div>
        <span className={`status-chip status-chip--${st.chip}`}>{st.label}</span>
        {onDelete && <button type="button" className="meetingcard__del" title="Remove this card" onClick={() => onDelete(message)}>✕</button>}
      </div>

      {isVideo && doc.assetUrl ? (
        <video src={doc.assetUrl} controls style={{ width: "100%", borderRadius: 10, marginTop: 8, maxHeight: 320 }} />
      ) : isCarousel ? (
        <div style={{ display: "flex", gap: 6, overflowX: "auto", marginTop: 8, paddingBottom: 4 }}>
          {frames.map((u, i) => (
            <div key={i} style={{ position: "relative", flex: "0 0 auto" }}>
              <img src={u} alt={`Slide ${i + 1}`} style={{ height: 120, borderRadius: 8, display: "block" }} />
              <span style={{ position: "absolute", top: 4, left: 4, background: "rgba(0,0,0,.7)", color: "#fff", fontSize: 11, borderRadius: 4, padding: "0 5px" }}>{i + 1}</span>
            </div>
          ))}
        </div>
      ) : (doc.thumbnailUrl || doc.assetUrl) ? (
        <img src={doc.thumbnailUrl || doc.assetUrl} alt={doc.title} style={{ width: "100%", borderRadius: 10, marginTop: 8, maxHeight: 320, objectFit: "cover" }} />
      ) : null}

      {doc.caption && (
        <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.45, opacity: 0.8, maxHeight: "5.8em", overflowY: "auto", paddingRight: 6 }}>
          {doc.caption}
        </p>
      )}

      {doc.status === "approved" && doc.approvedBy && (
        <p className="stat ok" style={{ marginTop: 6 }}>✓ Approved by {doc.approvedBy}</p>
      )}
      {doc.status === "changes_requested" && doc.reviewNote && (
        <div className="field__err" style={{ marginTop: 6 }}>💬 {doc.reviewNote}</div>
      )}

      {error && <div className="field__err">⚠ {error}</div>}

      {doc.canAct && (
        <div className="signoff__foot" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 10 }}>
          <button className="btn btn--sm" disabled={busy} onClick={() => setShowComment((s) => !s)}>Comment</button>
          <button className="btn btn--primary btn--sm" disabled={busy} onClick={approve}>
            {busy ? <span className="btn__spin" /> : "Approve"}
          </button>
        </div>
      )}

      {showComment && doc.canAct && (
        <div style={{ marginTop: 8 }}>
          <textarea rows={2} value={comment} placeholder="What would you like changed?"
            onChange={(e) => setComment(e.target.value)}
            style={{ width: "100%", resize: "vertical", padding: 8, borderRadius: 8, border: "1px solid var(--border, #ccc)", font: "inherit" }} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
            <button className="btn btn--sm" disabled={busy} onClick={() => { setShowComment(false); setComment(""); }}>Cancel</button>
            <button className="btn btn--primary btn--sm" disabled={busy || comment.trim().length < 2} onClick={sendComment}>
              {busy ? <span className="btn__spin" /> : "Send comment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
